import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft,
  DollarSign,
  Percent,
  Home,
  MapPin,
  Save
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia",
  "Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey",
  "New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina",
  "South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"
];

export default function SplitReportNewListing() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    street_address: '', city: '', state: '', zip_code: '',
    sales_price: '', status: 'Active',
    bedrooms: '', bathrooms: '', square_feet: '',
    buyer_agent_commission: '', sub_agent_commission: '', tc_commission: '',
    commission_mode: 'percent',
    notes: ''
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const formatPrice = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };

  const calcAmount = (pct) => {
    const price = parseFloat(form.sales_price);
    const p = parseFloat(pct);
    if (isNaN(price) || isNaN(p)) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price * p / 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ['street_address', 'city', 'state', 'zip_code', 'sales_price', 'bedrooms', 'bathrooms', 'square_feet', 'buyer_agent_commission', 'sub_agent_commission', 'tc_commission'];
    for (const field of required) {
      if (!form[field] && form[field] !== 0) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/split-report/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const listing = await res.json();
        toast.success('Listing added successfully!');
        navigate(`/split-report/listings/${listing.listing_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add listing');
      }
    } catch { toast.error('Failed to add listing'); }
    finally { setSubmitting(false); }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3D4F7A]/20 focus:border-[#3D4F7A] bg-white";
  const labelClass = "block text-sm font-medium text-slate-700 mb-1.5";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="split-report-new-listing">
      <div className="bg-gradient-to-r from-[#3D4F7A] to-[#2A3755] py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/split-report" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
              <div>
                <h1 className="font-heading text-3xl font-bold text-white">Add New Listing</h1>
                <p className="text-slate-300 mt-1">Share commission split data with the community</p>
              </div>
            </div>
            <Link to="/dashboard">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="sr-dashboard-btn">
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl py-6">
        <form onSubmit={handleSubmit}>
          {/* Property Address */}
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-4">
                <MapPin className="w-5 h-5 text-teal-500" />Property Address
              </h3>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Street Address *</label>
                  <input type="text" className={inputClass} value={form.street_address} onChange={(e) => update('street_address', e.target.value)} placeholder="123 Main Street" data-testid="listing-street" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className={labelClass}>City *</label>
                    <input type="text" className={inputClass} value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Dallas" data-testid="listing-city" />
                  </div>
                  <div>
                    <label className={labelClass}>State *</label>
                    <select className={inputClass} value={form.state} onChange={(e) => update('state', e.target.value)} data-testid="listing-state">
                      <option value="">Select</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Zip Code *</label>
                    <input type="text" className={inputClass} value={form.zip_code} onChange={(e) => update('zip_code', e.target.value)} placeholder="75201" data-testid="listing-zip" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Details */}
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 mb-4">
                <Home className="w-5 h-5 text-teal-500" />Property Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2">
                  <label className={labelClass}>Sales Price *</label>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="number" className={`${inputClass} pl-8`} value={form.sales_price} onChange={(e) => update('sales_price', e.target.value)} placeholder="450000" data-testid="listing-price" />
                  </div>
                  {form.sales_price && <p className="text-xs text-slate-500 mt-1">{formatPrice(form.sales_price)}</p>}
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Status *</label>
                  <select className={inputClass} value={form.status} onChange={(e) => update('status', e.target.value)} data-testid="listing-status">
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Sold">Sold</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Bedrooms *</label>
                  <input type="number" className={inputClass} value={form.bedrooms} onChange={(e) => update('bedrooms', e.target.value)} placeholder="3" data-testid="listing-beds" />
                </div>
                <div>
                  <label className={labelClass}>Bathrooms *</label>
                  <input type="number" className={inputClass} value={form.bathrooms} onChange={(e) => update('bathrooms', e.target.value)} placeholder="2" data-testid="listing-baths" />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Square Feet *</label>
                  <input type="number" className={inputClass} value={form.square_feet} onChange={(e) => update('square_feet', e.target.value)} placeholder="2200" data-testid="listing-sqft" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commission Splits */}
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <Percent className="w-5 h-5 text-teal-500" />Commission Splits
                </h3>
                <div className="flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="commission-mode-toggle">
                  <button type="button" onClick={() => update('commission_mode', 'percent')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${form.commission_mode === 'percent' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="mode-percent">
                    <Percent className="w-3 h-3 inline mr-1" />Percent
                  </button>
                  <button type="button" onClick={() => update('commission_mode', 'dollar')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${form.commission_mode === 'dollar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="mode-dollar">
                    <DollarSign className="w-3 h-3 inline mr-1" />Dollar
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Buyer's Agent {form.commission_mode === 'percent' ? '%' : '$'} *</label>
                  <div className="relative">
                    <input type="number" step={form.commission_mode === 'percent' ? '0.01' : '1'} min="0" max={form.commission_mode === 'percent' ? '100' : undefined} className={inputClass} value={form.buyer_agent_commission} onChange={(e) => update('buyer_agent_commission', e.target.value)} placeholder={form.commission_mode === 'percent' ? '3.0' : '13500'} data-testid="listing-buyer-pct" />
                    {form.commission_mode === 'percent' ? <Percent className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" /> : <DollarSign className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                  </div>
                  {form.commission_mode === 'percent' && form.buyer_agent_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {calcAmount(form.buyer_agent_commission)}</p>
                  )}
                  {form.commission_mode === 'dollar' && form.buyer_agent_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {(parseFloat(form.buyer_agent_commission) / parseFloat(form.sales_price) * 100).toFixed(2)}%</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Sub-Agent {form.commission_mode === 'percent' ? '%' : '$'} *</label>
                  <div className="relative">
                    <input type="number" step={form.commission_mode === 'percent' ? '0.01' : '1'} min="0" max={form.commission_mode === 'percent' ? '100' : undefined} className={inputClass} value={form.sub_agent_commission} onChange={(e) => update('sub_agent_commission', e.target.value)} placeholder={form.commission_mode === 'percent' ? '1.5' : '6750'} data-testid="listing-sub-pct" />
                    {form.commission_mode === 'percent' ? <Percent className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" /> : <DollarSign className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                  </div>
                  {form.commission_mode === 'percent' && form.sub_agent_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {calcAmount(form.sub_agent_commission)}</p>
                  )}
                  {form.commission_mode === 'dollar' && form.sub_agent_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {(parseFloat(form.sub_agent_commission) / parseFloat(form.sales_price) * 100).toFixed(2)}%</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Transaction Coord. {form.commission_mode === 'percent' ? '%' : '$'} *</label>
                  <div className="relative">
                    <input type="number" step={form.commission_mode === 'percent' ? '0.01' : '1'} min="0" max={form.commission_mode === 'percent' ? '100' : undefined} className={inputClass} value={form.tc_commission} onChange={(e) => update('tc_commission', e.target.value)} placeholder={form.commission_mode === 'percent' ? '0.5' : '2250'} data-testid="listing-tc-pct" />
                    {form.commission_mode === 'percent' ? <Percent className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" /> : <DollarSign className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                  </div>
                  {form.commission_mode === 'percent' && form.tc_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {calcAmount(form.tc_commission)}</p>
                  )}
                  {form.commission_mode === 'dollar' && form.tc_commission && form.sales_price && (
                    <p className="text-xs text-green-600 font-semibold mt-1">= {(parseFloat(form.tc_commission) / parseFloat(form.sales_price) * 100).toFixed(2)}%</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border border-slate-200 mb-6">
            <CardContent className="p-6">
              <label className={labelClass}>Notes (Optional)</label>
              <textarea
                className={`${inputClass} min-h-[80px] resize-none`}
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                placeholder="Any additional details about this listing..."
                data-testid="listing-notes"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={submitting} className="bg-teal-500 hover:bg-teal-600 px-8 py-5 text-base" data-testid="listing-submit">
              <Save className="w-4 h-4 mr-2" />{submitting ? 'Saving...' : 'Save Listing'}
            </Button>
            <Link to="/split-report">
              <Button type="button" variant="outline" className="py-5">Cancel</Button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
